import { useMutation, useQuery } from '@apollo/react-hooks'
import { gql } from 'apollo-boost'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography
} from '@paintscout/ui'
import type { DialogProps } from '@paintscout/ui'
import { useDialogs } from '@paintscout/ui-business'
import type { QuoteDocument } from 'paintscout'
import CloseIcon from '@material-ui/icons/Close'
import React, { useState } from 'react'
import { useSnackbar } from 'notistack'
import { useQuote } from '../../context'

const CHECK_PROCESSING_PAYMENT_CANCELABLE = gql`
  query checkProcessingPaymentCancelable($activityId: String!) {
    checkProcessingPaymentCancelable(activityId: $activityId)
  }
`

const CANCEL_PROCESSING_PAYMENT = gql`
  mutation cancelProcessingPayment($activityId: String!) {
    cancelProcessingPayment(activityId: $activityId) {
      updatedQuote
    }
  }
`

export interface CancelProcessingPaymentDialogProps extends DialogProps {
  activityId: string
  /** "ACH" or "EFT", matching the company's Payrix country. */
  achLabel: string
  /** Called with the updated quote once the payment is canceled. */
  onCanceled?: (updatedQuote: QuoteDocument) => void
}

/**
 * Confirms canceling a processing ACH payment. The dialog opens at once and asks the API for the
 * live Payrix status: a spinner while it checks, then either the confirmation or a message. A txn
 * that is already captured or settled gets a "contact support" message instead of a confirmation.
 */
export default function CancelProcessingPaymentDialog({
  open,
  activityId,
  achLabel,
  onCanceled,
  ...baseDialogProps
}: CancelProcessingPaymentDialogProps) {
  const { dismissDialog, dismissAllDialogs } = useDialogs()
  const { updateQuote } = useQuote()
  const { enqueueSnackbar } = useSnackbar()
  const [cancelError, setCancelError] = useState<any>(null)

  const { loading: checking, error: checkError } = useQuery(CHECK_PROCESSING_PAYMENT_CANCELABLE, {
    variables: { activityId },
    fetchPolicy: 'network-only'
  })
  const [cancelPayment, { loading: canceling }] = useMutation(CANCEL_PROCESSING_PAYMENT)

  async function handleConfirm() {
    try {
      const result = await cancelPayment({ variables: { activityId } })
      const updatedQuote: QuoteDocument = result.data?.cancelProcessingPayment?.updatedQuote
      if (updatedQuote) {
        onCanceled?.(updatedQuote)
        await updateQuote({ quote: updatedQuote, autosave: false, blockDirty: true })
      }
      enqueueSnackbar('Payment canceled', { variant: 'success' })
      dismissAllDialogs()
    } catch (error) {
      // The txn can settle between the check and the click, so show this like a failed check.
      setCancelError(error)
    }
  }

  const error = checkError ?? cancelError
  const graphQLError = (error as any)?.graphQLErrors?.[0]
  const isSettled = graphQLError?.extensions?.code === 'PAYMENT_SETTLED'
  const errorMessage = isSettled
    ? 'This payment is already in progress and can no longer be canceled here. Please contact support.'
    : graphQLError?.message ?? 'Something went wrong. Please try again or contact support.'

  let title = `Confirm Cancel ${achLabel} Payment`
  let content: React.ReactNode
  let actions: React.ReactNode

  if (checking) {
    title = 'Checking Payment'
    content = (
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '8px 0' }}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="textSecondary">
          Checking the payment status…
        </Typography>
      </div>
    )
  } else if (error) {
    title = isSettled ? 'Payment In Progress' : 'Could Not Cancel Payment'
    content = <Typography variant="body2">{errorMessage}</Typography>
    actions = (
      <Button variant="contained" onClick={dismissAllDialogs}>
        Close
      </Button>
    )
  } else {
    content = (
      <Typography variant="body2">
        {`This ${achLabel} payment has not gone through yet. Canceling it stops the payment, and no funds will be collected.`}
      </Typography>
    )
    actions = (
      <Button variant="contained" onClick={handleConfirm} loading={canceling}>
        Cancel Payment
      </Button>
    )
  }

  return (
    <Dialog open={open} maxWidth="xs" fullWidth {...baseDialogProps}>
      <DialogTitle
        rightContent={
          <IconButton size="small" onClick={dismissDialog} disabled={canceling}>
            <CloseIcon />
          </IconButton>
        }
      >
        {title}
      </DialogTitle>
      <DialogContent>{content}</DialogContent>
      {actions && <DialogActions>{actions}</DialogActions>}
    </Dialog>
  )
}
